import React, { useState, useEffect, useRef } from 'react';
import { useUrlState } from '../../hooks/useUrlState';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TablePagination,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Checkbox,
  ListItemText,
  ListItemIcon,
  Autocomplete,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ShoppingCart as POIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';
import {
  getPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  submitPurchaseOrder,
  approvePurchaseOrder,
  sendPurchaseOrder,
  closePurchaseOrder,
  cancelPurchaseOrder,
  deletePurchaseOrder,
  getSuppliers,
  getShipmentsForPO,
  getShipment,
  listPoPayments,
  createShipment,
  updateShipment,
  deleteShipment,
  markShipmentDispatched,
  markShipmentArrived,
  cancelShipmentDispatch,
  uploadShipmentDocument,
  deleteShipmentDocument,
  shipmentDocumentUrl,
  formatCurrency,
  formatDate,
  getStatusColor,
  getGoodsReceipts
} from '../../services/procurementService';
import { StatusBadge, PageHeader, ActionBar, Timeline, EntityChip, SavedViews } from '../ds';
import { useEntityTimeline } from '../../services/auditTimelineService';
import { useListShortcuts } from '../common/GlobalShortcuts';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useBrand } from '../../context/BrandContext';
import api from '../../config/api';
import SupplierPicker from '../common/SupplierPicker';
import ConfirmDialog from '../common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import PoDocuments from './PoDocuments';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

async function openPoPdf(po, onError) {
  try {
    const pnum = encodeURIComponent(po.po_number || '');
    const resp = await api.get(`${API_BASE}/api/procurement/purchase-orders/${po.id}/pdf?po_number=${pnum}`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `PurchaseOrder-${po.po_number || po.id}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    onError?.(e.response?.data?.error || e.message);
  }
}

// Canonical lifecycle (post 2026-05-18 rename): draft → open → partially_received → closed
//                                                                                ↘ cancelled
// Legacy values kept in the filter list so old saved-view chips and CIN7-imported
// rows still render: submitted, approved, sent, completed, received, ordered, shipped.
// All legacy "active" values are treated the same as `open`; legacy "done" values
// the same as `closed`. The backend has already migrated the DB rows.
// Status filter options — trimmed 2026-05-29 (Rishabh) to the statuses the PO
// workflow actually produces + any legacy value still present in live data.
// 'approved' kept only because one legacy-imported PO still uses it (reclassify
// to 'open' to retire it). Dropped pure-legacy values with zero live rows:
// submitted, sent, completed, received, ordered, shipped. The backend still
// recognises those if they ever reappear — this only trims the picker.
const PO_STATUSES = [
  // 'received' is re-added (Rishabh 2026-07-02): reopening a fully-received PO
  // sets status back to 'received', so live rows DO exist — dropping it from the
  // filter silently hid those POs from the default list ("the PO disappeared").
  'draft', 'open', 'in_transit', 'partially_received', 'received', 'closed', 'cancelled',
  'approved',
];
// Default for the status multi-select: everything EXCEPT cancelled — matches
// NetSuite/CIN7 "active by default" convention. Hoisted out of the function
// component so useUrlState's value===defaultValue identity check works
// reliably and the string isn't re-allocated on every render.
const STATUS_FILTER_DEFAULT = PO_STATUSES.filter(s => s !== 'cancelled').join(',');
const PO_STATUS_STEPS = ['draft', 'open', 'partially_received', 'closed'];
const PO_ACTIVE_STATUSES = ['open', 'in_transit', 'partially_received', 'approved', 'submitted', 'sent', 'ordered', 'shipped'];
const PO_DONE_STATUSES = ['closed', 'completed', 'received'];
// Statuses where header/items can still be edited. Terminal states (closed,
// completed, cancelled) are locked; everything else lets the operator open the
// edit form. Backend separately gates item-level edits when goods_receipts
// exist (see service.updatePurchaseOrder ITEMS_HAVE_RECEIPTS guard).
const PO_EDITABLE_STATUSES = ['draft', 'open', 'partially_received', 'submitted', 'approved', 'sent'];
const isPoEditable = (status) => PO_EDITABLE_STATUSES.includes(String(status || '').toLowerCase());
const isPoActive = (status) => PO_ACTIVE_STATUSES.includes(String(status || '').toLowerCase());
const isPoDone = (status) => PO_DONE_STATUSES.includes(String(status || '').toLowerCase());

function PurchaseOrders() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedBrand, brands } = useBrand();

  const [loading, setLoading] = useState(true);
  // initialLoaded flips true after the first successful list/detail fetch.
  // Used to gate the full-page spinner so subsequent filter-driven refetches
  // (search debounce, supplier/status change) DON'T blank the page —
  // Rishabh's "page reloads when I type" complaint 2026-05-22.
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // List view state
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  // Company's default reporting currency (companies_v2.default_currency, surfaced
  // by GET /api/company-settings). Used as the fallback whenever a PO/supplier row
  // has no currency of its own — never hardcode 'GBP'/'USD' for display. 'USD' here
  // is only a pre-fetch placeholder; loadCompanyCurrency() overwrites it on mount.
  const [companyCurrency, setCompanyCurrency] = useState('USD');
  // URL-bound filter state — survives browser back/forward and is shareable
  // via copy-paste URL. Same hook used in PendingShip / ReadyToInvoice.
  const [searchTerm, setSearchTerm] = useUrlState('q', '');
  // Local typing buffer — the search TextField writes here on every keystroke,
  // and a debounced useEffect below mirrors it to searchTerm (which is in the
  // useEffect deps that drives the API fetch). Without this, each keystroke
  // triggered an immediate refetch + URL update, giving Rishabh the
  // "page-refreshing-on-every-letter" feel he reported 2026-05-22.
  const [searchInput, setSearchInput] = useState(searchTerm || '');
  const [statusFilter, setStatusFilter] = useUrlState('status', STATUS_FILTER_DEFAULT);
  const [supplierFilter, setSupplierFilter] = useUrlState('supplier', '');
  const [brandFilter, setBrandFilter] = useUrlState('brand', '');
  const [currencyFilter, setCurrencyFilter] = useUrlState('cur', '');
  const [dateFromFilter, setDateFromFilter] = useUrlState('from', '');
  const [dateToFilter, setDateToFilter] = useUrlState('to', '');
  const [page, setPage] = useUrlState('p', 0, { parse: (v) => parseInt(v, 10) || 0 });
  const [rowsPerPage, setRowsPerPage] = useUrlState('pp', 25, { parse: (v) => parseInt(v, 10) || 25 });
  const [exporting, setExporting] = useState(false);

  // Remember the list's filter query-string so the PO detail's Back arrow
  // returns to the SAME filtered view instead of the whole unfiltered list
  // (Rishabh 2026-07-02). Persisted because opening a PO pushes the detail URL
  // (which carries no filters) over the list URL. Mirrors the Transfers fix.
  useEffect(() => {
    if (!id) {
      try { sessionStorage.setItem('scm.poListSearch', window.location.search || ''); } catch (_) { /* private mode */ }
    }
  }, [id, searchTerm, statusFilter, supplierFilter, brandFilter, currencyFilter, dateFromFilter, dateToFilter, page, rowsPerPage]);

  // Debounce searchInput → searchTerm: commit the typed value to the URL-bound
  // searchTerm 400ms after the user stops typing. Reading-state stays warm; the
  // expensive list fetch only fires once per pause. If the URL searchTerm
  // changes externally (browser back/forward), the input mirrors it on mount
  // via the useState initial above; we don't re-sync mid-typing.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchTerm) setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Detail view state
  const [selectedPO, setSelectedPO] = useState(null);
  // Read-only payments list shown alongside the PO detail. Hits the same
  // `listPoPayments` endpoint the edit page uses — no DB change, just
  // surfaces what's already there. Added 2026-05-22 per Rishabh: he couldn't
  // see CIN7-backfilled payments on EF-14031 because the detail view never
  // rendered them, only the edit view did. Inconsistent UX, now unified.
  const [poPayments, setPoPayments] = useState([]);
  // Audit D4: goods receipts linked to the open PO (loaded with the detail).
  const [poReceipts, setPoReceipts] = useState([]);
  // Shipments (Tier 1 read-only). Each PO has 1..N shipments; default is 1.
  // `shipments` is the header list; `shipmentDetails[id]` caches per-shipment
  // lines (lazy-loaded on expansion). `expandedShipments[id]` drives the
  // accordion open/closed state — first shipment auto-expands on PO load.
  const [shipments, setShipments] = useState([]);
  const [shipmentDetails, setShipmentDetails] = useState({});
  const [expandedShipments, setExpandedShipments] = useState({});
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  // Tier 2: shipment write dialogs. `shipmentDialog` is { mode: 'create'|'edit',
  // shipmentId?: number, ... }. Create-mode shows the line-split picker;
  // edit-mode shows only header fields. `shipmentForm` is the working draft.
  const [shipmentDialog, setShipmentDialog] = useState(null);
  const [shipmentForm, setShipmentForm] = useState({});
  const [shipmentSplitLines, setShipmentSplitLines] = useState({});  // { po_item_id: qty }
  const [shipmentBusy, setShipmentBusy] = useState(false);
  // Two-stage receipt: dispatch dialog state. dispatchDialog is { shipment } or null.
  // dispatchForm holds the operator-editable fields before submit.
  const [dispatchDialog, setDispatchDialog] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({});

  // Shared MUI confirm — replaces the native window.confirm/prompt popups that
  // used to guard the destructive shipment/PO actions below. Same primitive as
  // Suppliers, ProductCOGS, RetailOrders et al, so every confirm in the app
  // looks and behaves the same.
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editingPO, setEditingPO] = useState(null);
  // poForm state — note: internal_notes is included; persists as part of PO header
  const [poForm, setPoForm] = useState({
    supplier_id: '',
    po_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    shipping_address: '',
    notes: '',
    internal_notes: '',
    currency: 'USD',
    // CIN7-parity additions (2026-05-14): commercials + logistics on the PO
    // header. payment_terms defaults to backend default ('Net 30').
    payment_terms: 'Net 30',
    tracking_number: '',
    carrier: '',
    bill_of_lading: '',
    incoterms: '',
    manufacturing_coo: '',
    // Customs / transit dates (added later same day) — for international
    // sourcing visibility. Empty string = NULL on backend.
    ex_factory_date: '',
    port_of_loading: '',
    port_of_discharge: '',
    eta_port_discharge: '',
    items: [{ sku: '', product_name: '', supplier_sku: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, notes: '' }]
  });

  useEffect(() => {
    loadSuppliers();
    loadCompanyCurrency();
  }, []);

  useEffect(() => {
    if (id) {
      loadPODetails(id);
    } else {
      loadPurchaseOrders();
    }
    // Refetch on any filter or pagination change. Server-side pagination —
    // page * rowsPerPage flows to the backend offset so the browser only
    // holds one page's worth of rows in state at a time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedBrand?.id, page, rowsPerPage, statusFilter, supplierFilter, brandFilter, searchTerm, currencyFilter, dateFromFilter, dateToFilter]);

  // Pull the company's default reporting currency once so every currency
  // fallback in this screen tracks the actual company setting (dynamic), instead
  // of a hardcoded literal. Silent-fail keeps the placeholder 'USD' on error.
  const loadCompanyCurrency = async () => {
    try {
      const res = await api.get(`${API_BASE}/api/company-settings`);
      // Response shape: { success: true, data: { ...settings, defaultCurrency } }
      const ccy = res?.data?.data?.defaultCurrency;
      if (ccy) setCompanyCurrency(String(ccy).toUpperCase());
    } catch (err) {
      console.error('Failed to load company currency:', err);
    }
  };

  const loadSuppliers = async () => {
    try {
      // 500 cap so the multi-checkbox filter sees every active supplier (we
      // have ~88 today; bumped from 100 to give headroom + capture inactive
      // ones on POs from before they were deactivated). The list is used both
      // for the filter dropdown and for the "create PO" supplier picker.
      const res = await getSuppliers({ is_active: true, limit: 500 });
      // Handle response structure: { success, data: suppliers_array } or { success, data: { suppliers: [] } }
      const suppliersData = res?.data;
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  };

  const loadPurchaseOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      // Server-side pagination: only fetch the current page. Total row count
      // comes back in `data.total` so the TablePagination footer is accurate.
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (statusFilter) params.status = statusFilter;
      if (supplierFilter) params.supplier_id = supplierFilter;
      if (brandFilter) params.brand_id = brandFilter;
      if (searchTerm) params.search = searchTerm;
      if (currencyFilter) params.currency = currencyFilter;
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;

      const res = await getPurchaseOrders(params);
      const posData = res?.data;
      const rows = Array.isArray(posData) ? posData : (posData?.purchase_orders || []);
      const total = Array.isArray(posData) ? rows.length : Number(posData?.total || rows.length);
      setPurchaseOrders(rows);
      setTotalRows(total);
      setInitialLoaded(true);
    } catch (err) {
      console.error('Failed to load purchase orders:', err);
      setError('Failed to load purchase orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Only the newest purchase order load may apply its answer: an older one landing late (the user opened another PO, or
  // went back to the list) would show the wrong PO under this URL (DT-1139, same as Transfers DT-1113). Its payments,
  // receipts and shipments follow the same rule.
  const detailRequestRef = useRef(0);
  useEffect(() => { if (!id) detailRequestRef.current += 1; }, [id]);

  const loadPODetails = async (poId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    // A different record: drop the previous one now, so a failed load never leaves it on screen under this URL.
    setSelectedPO(prev => (prev && String(prev.id) === String(poId) ? prev : null));
    setShipmentsLoading(false); // a replaced load may have left the previous PO's shipments spinner on
    setLoading(true);
    setError(null);
    setPoReceipts([]); // clear the previous PO's receipts before the async load lands

    try {
      const res = await getPurchaseOrder(poId);
      if (stale()) return;
      // Backfill a blank currency with the company default so every
      // formatCurrency(..., selectedPO.currency) in the detail panel renders in
      // the company's currency rather than the helper's literal fallback.
      const po = res?.data;
      if (po && !po.currency) po.currency = companyCurrency;
      setSelectedPO(po);
      setInitialLoaded(true);
      // Fire-and-forget payments load — fails silently if endpoint errors
      // (the PO view should still render even without payments).
      listPoPayments(poId).then(r => { if (!stale()) setPoPayments(r?.data || []); }).catch(() => { if (!stale()) setPoPayments([]); });
      // Audit D4: linked goods receipts. The header actual_delivery_date is
      // proven unreliable fleet-wide (missing on 85/342 POs with receipts,
      // disagrees with the receipts on 225/342 — it's only stamped by a manual
      // operator action), so received dates are DERIVED from the receipts here.
      getGoodsReceipts({ po_id: poId, limit: 100 })
        .then(r => {
          if (stale()) return;
          const list = r?.data?.goods_receipts || r?.goods_receipts || [];
          setPoReceipts(Array.isArray(list) ? list : []);
        })
        .catch((err) => { if (stale()) return; console.error('Failed to load PO receipts:', err); setPoReceipts([]); });
      // Fire-and-forget: load shipments in parallel. The Shipments card below
      // the Line Items table renders from this state; failures are non-fatal
      // (legacy POs may have no shipment row if migration was skipped).
      loadShipmentsForPO(poId, stale);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load PO details:', err);
      setError('Failed to load purchase order details. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }
  };

  // isStale: set by loadPODetails, so an older PO's shipments never land on the PO now open.
  const loadShipmentsForPO = async (poId, isStale = () => false) => {
    setShipmentsLoading(true);
    try {
      const res = await getShipmentsForPO(poId);
      if (isStale()) return;
      const headers = res?.data || [];
      setShipments(headers);
      // Auto-expand and fetch lines for the first (default) shipment so the
      // landed-cost detail is visible without a click. Multi-shipment POs (Tier 2)
      // will keep subsequent shipments collapsed until the operator opens them.
      if (headers.length > 0) {
        const firstId = headers[0].id;
        const detail = await getShipment(firstId);
        if (isStale()) return;
        setShipmentDetails(prev => ({ ...prev, [firstId]: detail?.data }));
        setExpandedShipments(prev => ({ ...prev, [firstId]: true }));
      }
    } catch (err) {
      if (isStale()) return;
      console.warn('[shipments.load]', err?.response?.data?.error || err.message);
      setShipments([]);
    } finally {
      if (!isStale()) setShipmentsLoading(false);
    }
  };

  const toggleShipmentExpansion = async (shipmentId) => {
    const willExpand = !expandedShipments[shipmentId];
    setExpandedShipments(prev => ({ ...prev, [shipmentId]: willExpand }));
    // Lazy-load detail (lines) on first expand.
    if (willExpand && !shipmentDetails[shipmentId]) {
      try {
        const detail = await getShipment(shipmentId);
        setShipmentDetails(prev => ({ ...prev, [shipmentId]: detail?.data }));
      } catch (err) {
        console.warn('[shipments.detail]', err?.response?.data?.error || err.message);
      }
    }
  };

  // Open "Add shipment" dialog. Pre-loads the default shipment's lines so the
  // operator can pick which lines + qty to split into the new shipment.
  const openCreateShipmentDialog = async () => {
    if (!shipments.length) return;
    // DT-1922: clear before the load, so 'Failed to load default shipment lines' (set below, the dialog still opens)
    // stays and shows in the dialog.
    setError(null);
    const defaultShipment = shipments[0];
    // Ensure detail (lines) is loaded.
    let detail = shipmentDetails[defaultShipment.id];
    if (!detail) {
      try {
        const r = await getShipment(defaultShipment.id);
        detail = r?.data;
        setShipmentDetails(prev => ({ ...prev, [defaultShipment.id]: detail }));
      } catch (err) {
        setError('Failed to load default shipment lines');
        return;
      }
    }
    setShipmentForm({
      po_id: selectedPO.id,
      destination_warehouse_code: '',
      transport_mode: '',
      carrier: '',
      tracking_number: '',
      eta: '',
      etd: '',
      ex_factory_date: '',
      freight_cost: 0,
      customs_duty: 0,
      insurance_cost: 0,
      other_costs: 0,
      costs_currency: defaultShipment.costs_currency || selectedPO.currency || 'USD',
      allocation_method: 'by_value',
      status: 'planned',
      notes: '',
    });
    setShipmentSplitLines({});
    setShipmentDialog({ mode: 'create', defaultShipmentId: defaultShipment.id });
  };

  // Open "Edit shipment" dialog for header-only edits (freight/customs/dates etc.).
  const openEditShipmentDialog = async (shipmentId) => {
    let detail = shipmentDetails[shipmentId];
    if (!detail) {
      try {
        const r = await getShipment(shipmentId);
        detail = r?.data;
        setShipmentDetails(prev => ({ ...prev, [shipmentId]: detail }));
      } catch (err) {
        setError('Failed to load shipment'); return;
      }
    }
    setShipmentForm({
      destination_warehouse_code: detail.destination_warehouse_code || '',
      transport_mode: detail.transport_mode || '',
      carrier: detail.carrier || '',
      tracking_number: detail.tracking_number || '',
      bill_of_lading: detail.bill_of_lading || '',
      port_of_loading: detail.port_of_loading || '',
      port_of_discharge: detail.port_of_discharge || '',
      ex_factory_date: detail.ex_factory_date || '',
      etd: detail.etd || '',
      eta: detail.eta || '',
      freight_cost: Number(detail.freight_cost) || 0,
      customs_duty: Number(detail.customs_duty) || 0,
      insurance_cost: Number(detail.insurance_cost) || 0,
      other_costs: Number(detail.other_costs) || 0,
      costs_currency: detail.costs_currency || 'USD',
      allocation_method: detail.allocation_method || 'by_value',
      status: detail.status || 'planned',
      notes: detail.notes || '',
    });
    setError(null); setShipmentDialog({ mode: 'edit', shipmentId });
  };

  const submitShipmentDialog = async () => {
    setShipmentBusy(true);
    setError(null);
    try {
      if (shipmentDialog.mode === 'create') {
        // Build lines array from the split picker. Only lines with qty > 0
        // are included; the rest stay on the default shipment.
        const lines = Object.entries(shipmentSplitLines)
          .map(([po_item_id, qty]) => ({ po_item_id: parseInt(po_item_id, 10), quantity_shipped: Number(qty) || 0 }))
          .filter(l => l.quantity_shipped > 0);
        if (!lines.length) throw new Error('Pick at least one line + qty to split into the new shipment');
        await createShipment({ ...shipmentForm, lines });
      } else {
        await updateShipment(shipmentDialog.shipmentId, shipmentForm);
      }
      // Refresh shipment list + details from server.
      setShipmentDetails({});  // invalidate cache
      await loadShipmentsForPO(selectedPO.id);
      setShipmentDialog(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setShipmentBusy(false); }
  };

  const handleDeleteShipment = async (shipmentId) => {
    const ok = await confirm({
      title: 'Delete this shipment?',
      message: 'The quantities on its lines move back to the default shipment. Nothing is received or un-received.',
      confirmText: 'Delete shipment',
      severity: 'danger',
    });
    if (!ok) return;
    setShipmentBusy(true);
    try {
      await deleteShipment(shipmentId);
      setShipmentDetails({});
      await loadShipmentsForPO(selectedPO.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setShipmentBusy(false); }
  };

  // Two-stage receipt: open the dispatch dialog with sensible defaults
  // pulled from the shipment row (operator can override).
  const openDispatchDialog = (shipment) => {
    setError(null); setDispatchDialog({ shipment });
    setDispatchForm({
      dispatched_at: new Date().toISOString().slice(0, 10),
      bill_of_lading: shipment.bill_of_lading || '',
      carrier: shipment.carrier || '',
      vessel_or_flight: shipment.vessel_or_flight || '',
      incoterms: shipment.incoterms || selectedPO?.incoterms || '',
      port_of_loading: shipment.port_of_loading || '',
      port_of_discharge: shipment.port_of_discharge || '',
      etd: shipment.etd ? String(shipment.etd).slice(0, 10) : '',
      eta: shipment.eta ? String(shipment.eta).slice(0, 10) : '',
      // Landed-cost numbers are pre-filled from existing shipment row.
      // Operator can update if a fresher freight estimate is available.
      freight_cost: Number(shipment.freight_cost) || 0,
      customs_duty: Number(shipment.customs_duty) || 0,
      insurance_cost: Number(shipment.insurance_cost) || 0,
      other_costs: Number(shipment.other_costs) || 0,
      costs_currency: shipment.costs_currency || selectedPO?.currency || 'USD',
    });
  };
  const submitDispatch = async () => {
    if (!dispatchDialog?.shipment) return;
    setShipmentBusy(true);
    try {
      const result = await markShipmentDispatched(dispatchDialog.shipment.id, dispatchForm);
      setSuccess(
        `Shipment dispatched. GIT JE posted: ${result.data?.dispatched_value_currency || ''} ${
          Number(result.data?.dispatched_value_base_ccy || 0).toFixed(2)
        } (Dr 1220 / Cr 2150).`
      );
      setDispatchDialog(null);
      setShipmentDetails({});
      await loadShipmentsForPO(selectedPO.id);
      await loadPurchaseOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setShipmentBusy(false); }
  };
  const handleMarkArrived = async (shipmentId) => {
    const ok = await confirm({
      title: 'Mark shipment as arrived?',
      message: 'Records that the shipment reached the destination port. This is a status change only — no journal entry is posted and stock is not affected.',
      confirmText: 'Mark arrived',
      severity: 'info',
    });
    if (!ok) return;
    setShipmentBusy(true);
    try {
      await markShipmentArrived(shipmentId);
      setSuccess('Shipment marked arrived.');
      setShipmentDetails({});
      await loadShipmentsForPO(selectedPO.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setShipmentBusy(false); }
  };
  const handleCancelDispatch = async (shipmentId) => {
    // Input mode resolves { value } (so a blank reason is still a confirm —
    // matching the old window.prompt, where only Cancel returned null).
    const res = await confirm({
      title: 'Cancel this dispatch?',
      message: 'The goods go back to not-dispatched and a reversing journal entry is posted (Dr 2150 / Cr 1220).',
      confirmText: 'Cancel dispatch',
      cancelText: 'Keep dispatch',
      severity: 'warning',
      input: {
        label: 'Reason',
        placeholder: 'e.g. lost in transit, supplier cancelled',
        multiline: true,
      },
    });
    if (!res) return;
    const reason = res.value;
    setShipmentBusy(true);
    try {
      await cancelShipmentDispatch(shipmentId, reason);
      setSuccess('Dispatch cancelled. Reversing JE posted (Dr 2150 / Cr 1220).');
      setShipmentDetails({});
      await loadShipmentsForPO(selectedPO.id);
      await loadPurchaseOrders();
    } catch (err) {
      const data = err.response?.data;
      let msg = data?.error || err.message;
      if (data?.blockers?.length) {
        msg += `\n\nLinked goods receipts must be voided first:\n${
          data.blockers.map(b => `• ${b.grn_number || ('GR-' + b.id)} (${b.inspection_status})`).join('\n')
        }`;
      }
      setError(msg);
    } finally { setShipmentBusy(false); }
  };
  // Document upload helper. docType ∈ {supplier_ci, packing_list, bill_of_lading}.
  const handleShipmentDocUpload = async (shipmentId, docType, file) => {
    if (!file) return;
    setShipmentBusy(true);
    try {
      await uploadShipmentDocument(shipmentId, docType, file);
      setSuccess(`${docType.replace(/_/g, ' ')} uploaded.`);
      setShipmentDetails({});
      await loadShipmentsForPO(selectedPO.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setShipmentBusy(false); }
  };
  const handleShipmentDocDelete = async (shipmentId, docType) => {
    const ok = await confirm({
      title: 'Delete this document?',
      message: `The ${docType.replace(/_/g, ' ')} document will be removed from this shipment. You can upload it again afterwards.`,
      confirmText: 'Delete document',
      severity: 'danger',
    });
    if (!ok) return;
    setShipmentBusy(true);
    try {
      await deleteShipmentDocument(shipmentId, docType);
      setSuccess('Document deleted.');
      setShipmentDetails({});
      await loadShipmentsForPO(selectedPO.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally { setShipmentBusy(false); }
  };

  const handleOpenDialog = (po = null) => {
    if (po) {
      setEditingPO(po);
      setPoForm({
        supplier_id: po.supplier_id || '',
        po_date: po.po_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        expected_delivery_date: po.expected_delivery_date?.split('T')[0] || '',
        shipping_address: po.shipping_address || '',
        notes: po.notes || '',
        internal_notes: po.internal_notes || '',
        currency: po.currency || companyCurrency,
        payment_terms: po.payment_terms || 'Net 30',
        tracking_number: po.tracking_number || '',
        carrier: po.carrier || '',
        bill_of_lading: po.bill_of_lading || '',
        incoterms: po.incoterms || '',
        manufacturing_coo: po.manufacturing_coo || '',
        ex_factory_date: po.ex_factory_date?.split('T')[0] || '',
        port_of_loading: po.port_of_loading || '',
        port_of_discharge: po.port_of_discharge || '',
        eta_port_discharge: po.eta_port_discharge?.split('T')[0] || '',
        items: po.items?.length > 0 ? po.items.map(i => ({
          sku: i.sku || '',
          product_name: i.product_name || '',
          supplier_sku: i.supplier_sku || '',
          quantity: Number(i.ordered_quantity ?? i.quantity ?? 0),
          unit_price: Number(i.unit_cost ?? i.unit_price ?? 0),
          discount_percent: Number(i.discount_percent || 0),
          tax_percent: Number(i.tax_percent || 0),
          notes: i.notes || '',
        })) : [{ sku: '', product_name: '', supplier_sku: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, notes: '' }]
      });
    } else {
      setEditingPO(null);
      setPoForm({
        supplier_id: '',
        po_date: new Date().toISOString().split('T')[0],
        expected_delivery_date: '',
        shipping_address: '',
        notes: '',
        internal_notes: '',
        currency: companyCurrency,
        payment_terms: 'Net 30',
        tracking_number: '',
        carrier: '',
        bill_of_lading: '',
        incoterms: '',
        manufacturing_coo: '',
        ex_factory_date: '',
        port_of_loading: '',
        port_of_discharge: '',
        eta_port_discharge: '',
        items: [{ sku: '', product_name: '', supplier_sku: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, notes: '' }]
      });
    }
    setError(null); setDialogOpen(true);
  };

  const handleAddItem = () => {
    setPoForm({
      ...poForm,
      items: [...poForm.items, { sku: '', product_name: '', supplier_sku: '', quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0, notes: '' }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = poForm.items.filter((_, i) => i !== index);
    setPoForm({ ...poForm, items: newItems });
  };

  const handleUpdateItem = (index, field, value) => {
    const newItems = [...poForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setPoForm({ ...poForm, items: newItems });
  };

  // line_total = qty × price × (1 − discount%) × (1 + tax%). Matches the
  // backend formula in service.createPurchaseOrder so the header total stays
  // in sync with the per-line breakdown. Per-line totals reflect tax inclusive
  // — header shows subtotal + taxes broken out.
  const computeLineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const disc = parseFloat(item.discount_percent) || 0;
    const tax = parseFloat(item.tax_percent) || 0;
    const gross = qty * price;
    const netAfterDisc = gross * (1 - disc / 100);
    return netAfterDisc * (1 + tax / 100);
  };
  const calculateTotal = () => {
    return poForm.items.reduce((total, item) => total + computeLineTotal(item), 0);
  };

  const handleSave = async () => {
    try {
      setError(null);
      const poData = {
        ...poForm,
        brand_id: selectedBrand?.id
      };

      if (editingPO) {
        await updatePurchaseOrder(editingPO.id, poData);
        setSuccess('Purchase order updated successfully');
      } else {
        await createPurchaseOrder(poData);
        setSuccess('Purchase order created successfully');
      }
      setDialogOpen(false);
      if (id) {
        loadPODetails(id);
      } else {
        loadPurchaseOrders();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save purchase order');
    }
  };

  const handleSubmit = async (poId) => {
    try {
      await submitPurchaseOrder(poId);
      setSuccess('Purchase order submitted for approval');
      loadPODetails(poId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit purchase order');
    }
  };

  const handleApprove = async (poId) => {
    try {
      await approvePurchaseOrder(poId, 1); // TODO: Use actual user ID
      setSuccess('Purchase order approved');
      loadPODetails(poId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to approve purchase order');
    }
  };

  const handleSend = async (poId) => {
    try {
      await sendPurchaseOrder(poId);
      setSuccess('Purchase order sent to supplier');
      loadPODetails(poId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send purchase order');
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }
    try {
      await cancelPurchaseOrder(selectedPO.id, cancelReason);
      setSuccess('Purchase order cancelled');
      setCancelDialogOpen(false);
      setCancelReason('');
      loadPODetails(selectedPO.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel purchase order');
    }
  };

  // Hard delete — backend (purchase-orders/service.js#deletePurchaseOrder)
  // refuses if any non-rejected goods-receipt or any received_quantity
  // remains. Surface the blocker message verbatim so operator knows what
  // to void/reject first. After successful delete, navigate back to list.
  // deleteBlockers captures the structured blocker list from the backend's
  // 409 response so the Alert can render clickable links per blocker
  // (Rishabh request 2026-05-22 — text-only blocker message had no way to
  // jump to the GR/payment record blocking the delete).
  const [deleteBlockers, setDeleteBlockers] = useState(null);
  const handleDeletePO = async () => {
    if (!selectedPO) return;
    const ok = await confirm({
      title: `Permanently delete PO ${selectedPO.po_number}?`,
      message: 'This cannot be undone and leaves no audit trail. If you need a record of the order, use Void PO instead.',
      confirmText: 'Delete permanently',
      severity: 'danger',
    });
    if (!ok) return;
    setDeleteBlockers(null);
    try {
      await deletePurchaseOrder(selectedPO.id);
      setSuccess(`PO ${selectedPO.po_number} deleted`);
      navigate('/procurement/purchase-orders');
    } catch (err) {
      const apiErr = err.response?.data;
      setError(apiErr?.error || err.message || 'Failed to delete purchase order');
      // Structured blockers (when present) — used by the link-list below.
      if (apiErr?.blockers && typeof apiErr.blockers === 'object') {
        setDeleteBlockers(apiErr.blockers);
      } else {
        setDeleteBlockers(null);
      }
    }
  };

  const getStatusStep = (status) => {
    const index = PO_STATUS_STEPS.indexOf(status);
    return index >= 0 ? index : 0;
  };

  // Server-side search covers po_number/supplier/notes/tracking, so this
  // client-side pass is a no-op pass-through. Kept as a list reference so the
  // table render code below doesn't need to change.
  const filteredPOs = purchaseOrders;

  // Activity timeline for this PO — must be called unconditionally (rules-of-hooks).
  const { events: poTimelineEvents, loading: poTimelineLoading } = useEntityTimeline(
    'procurement_purchase_orders_route',
    selectedPO?.id || null
  );

  // List-page shortcuts: `n` creates new PO when on the list page.
  useListShortcuts({
    onNew: () => { if (!id) navigate('/procurement/purchase-orders/new'); },
    onRefresh: () => { if (!id) loadPurchaseOrders(); },
  });

  // Full-page spinner ONLY on first ever load. Subsequent refetches (search
  // debounce, filter change, supplier flip) keep the existing UI mounted and
  // surface progress via the LinearProgress strip on the filter card —
  // otherwise Rishabh saw the page "reload" every 400ms while typing.
  if (loading && !initialLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Detail View
  if (id && selectedPO) {
    const isCancelled = selectedPO.status === 'cancelled';

    // Back arrow → the SAME filtered list the user came from, not the whole
    // unfiltered list (Rishabh 2026-07-02). The list view stashed its filter
    // query-string in sessionStorage above; replay it here.
    let savedListSearch = '';
    try { savedListSearch = sessionStorage.getItem('scm.poListSearch') || ''; } catch (_) { /* private mode */ }
    const listBackTo = `/procurement/purchase-orders${savedListSearch}`;

    return (
      <Box>
        {/* Identity-first PageHeader: supplier name primary, PO number as slug,
            metadata pills for currency + total + created date. */}
        <PageHeader
          backTo={listBackTo}
          primary={selectedPO.supplier_name || `PO ${selectedPO.po_number}`}
          slug={selectedPO.po_number}
          metadata={[
            ...(selectedPO.currency && selectedPO.total_amount != null ? [{
              label: formatCurrency(selectedPO.total_amount, selectedPO.currency),
            }] : []),
            ...(selectedPO.created_at ? [{
              label: `Created ${formatDate(selectedPO.created_at)}`,
            }] : []),
            ...(selectedPO.warehouse_code ? [{
              label: selectedPO.warehouse_name || selectedPO.warehouse_code,
              tooltip: selectedPO.warehouse_name
                ? `${selectedPO.warehouse_code}${selectedPO.warehouse_city ? ` · ${selectedPO.warehouse_city}` : ''}`
                : 'Unmapped warehouse code',
            }] : []),
          ]}
          statusBadge={<StatusBadge status={selectedPO.status} size="medium" />}
          actions={
            <ActionBar
              primaryCount={(isPoEditable(selectedPO.status) || isPoDone(selectedPO.status)) ? 1 : 0}
              actions={[
                // Closed/completed/received POs are also openable for edit (the edit page
                // shows a "closed record" banner). Only cancelled stays non-editable.
                // Backend already permits updating a closed PO. (Rishabh 2026-07-01)
                ...((isPoEditable(selectedPO.status) || isPoDone(selectedPO.status)) ? [{
                  key: 'edit',
                  label: 'Edit',
                  icon: <EditIcon fontSize="small" />,
                  onClick: () => navigate(`/procurement/purchase-orders/${selectedPO.id}/edit`),
                }] : []),
                {
                  key: 'download-pdf',
                  label: 'Download PDF',
                  icon: <PrintIcon fontSize="small" />,
                  onClick: () => openPoPdf(selectedPO, setError),
                },
                // Intercompany-only: generate a Commercial Invoice from the PO.
                // Backend guards on supplier.is_intercompany=1; the action is
                // hidden entirely for external suppliers (the CI for those
                // continues to be uploaded as the supplier-sent PDF on the
                // shipment Documents card).
                ...(Number(selectedPO.supplier_is_intercompany) === 1 ? [{
                  key: 'generate-ci',
                  label: 'Generate Commercial Invoice',
                  icon: <DocumentIcon fontSize="small" />,
                  onClick: async () => {
                    try {
                      const resp = await api.post(`${API_BASE}/api/commercial-invoices/from-purchase-order/${selectedPO.id}`);
                      const ciId = resp?.data?.data?.id;
                      if (!ciId) { setError('Commercial invoice id missing in response'); return; }
                      setSuccess(resp?.data?.data?.reused ? 'Opened existing commercial invoice' : 'Commercial invoice generated');
                      navigate(`/inventory/commercial-invoices?focus=${ciId}`);
                    } catch (e) {
                      setError(e.response?.data?.error || e.message || 'Failed to generate commercial invoice');
                    }
                  },
                }] : []),
                {
                  key: 'print',
                  label: 'Quick print',
                  icon: <PrintIcon fontSize="small" />,
                  onClick: () => window.print(),
                },
                {
                  key: 'copy-po',
                  label: 'Copy PO number',
                  icon: <CopyIcon fontSize="small" />,
                  onClick: () => navigator.clipboard?.writeText(selectedPO.po_number || ''),
                },
                {
                  key: 'email-supplier',
                  label: 'Email supplier',
                  icon: <EmailIcon fontSize="small" />,
                  disabled: !selectedPO.supplier_name,
                  onClick: () => {
                    const subject = encodeURIComponent(`Purchase Order ${selectedPO.po_number || ''}`);
                    const body = encodeURIComponent(`Hi,\n\nRegarding purchase order ${selectedPO.po_number || ''}.\n\nThanks,`);
                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  },
                },
                // Delete is a hard-delete option. Backend refuses with a 409
                // listing the blocker GRNs if anything is still attached, so
                // we show it everywhere and let the operator try.
                {
                  key: 'delete-po',
                  label: 'Delete PO',
                  icon: <DeleteIcon fontSize="small" />,
                  onClick: handleDeletePO,
                },
              ]}
            />
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); setDeleteBlockers(null); }}>
            <Box>{error}</Box>
            {/* When the delete was rejected with structured blockers (409 from
                deletePurchaseOrder), render each blocker as a clickable link
                so the operator can jump straight to the GR/payment/invoice
                they need to void first — Rishabh fix 2026-05-22. */}
            {deleteBlockers && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {(deleteBlockers.goods_receipts || []).map(gr => (
                  <Typography key={`gr-${gr.id}`} variant="body2">
                    • Goods receipt{' '}
                    <Box component="a" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                         onClick={() => navigate(`/procurement/goods-receipts/${gr.id}`)}>
                      {gr.grn_number || `GR-${gr.id}`}
                    </Box>
                    {gr.inspection_status ? ` — ${gr.inspection_status}` : ''}
                    {gr.receipt_date ? ` (${formatDate(gr.receipt_date)})` : ''}
                  </Typography>
                ))}
                {(deleteBlockers.supplier_invoices || []).map(inv => (
                  <Typography key={`inv-${inv.id}`} variant="body2">
                    • Supplier invoice{' '}
                    <Box component="a" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                         onClick={() => navigate(`/finance/supplier-invoices/${inv.id}`)}>
                      {inv.invoice_number || `INV-${inv.id}`}
                    </Box>
                    {inv.amount ? ` — ${formatCurrency(inv.amount, selectedPO?.currency)}` : ''}
                    {inv.status ? ` (${inv.status})` : ''}
                  </Typography>
                ))}
                {(deleteBlockers.payments || []).map(p => (
                  <Typography key={`pay-${p.id}`} variant="body2">
                    • Payment id <strong>{p.id}</strong>
                    {p.amount ? ` — ${formatCurrency(p.amount, p.currency || selectedPO?.currency)}` : ''}
                    {p.payment_date ? ` on ${formatDate(p.payment_date)}` : ''}
                    {p.reference ? ` (ref: ${p.reference})` : ''}
                    {/* Payments UI lives on the PO edit page (PurchaseOrderEntry).
                        Link there so the operator can void/delete the row that's
                        blocking the PO delete. */}
                    {' · '}
                    <Box component="a" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                         onClick={() => navigate(`/procurement/purchase-orders/${selectedPO.id}/edit#payments`)}>
                      Open in edit view
                    </Box>
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {['git', 'manufacturer', 'supplier', 'legacy'].includes(String(selectedPO.warehouse_type || '').toLowerCase()) && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/procurement/purchase-orders/${selectedPO.id}/edit`)}
              >
                Set real destination
              </Button>
            }
          >
            <strong>Ship-to is a virtual warehouse ({selectedPO.warehouse_code} · {selectedPO.warehouse_type}).</strong>{' '}
            Customs / freight forwarders need a real physical address. PDF will fall back to the buyer subsidiary's
            address — update the receipt warehouse to a real one to clean this up.
          </Alert>
        )}

        {/* Status Stepper */}
        {!isCancelled && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stepper activeStep={getStatusStep(selectedPO.status)} alternativeLabel>
                {PO_STATUS_STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel sx={{ textTransform: 'capitalize' }}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {/* NOTE: do NOT gate this card on !isCompleted — a closed/completed PO
            still needs the Reopen action (e.g. a Cin7 PO closed too early). The
            inner buttons each self-gate by status, so on a closed PO only Reopen
            shows. Gating the whole card hid Reopen for exactly the POs that need
            it (Rishabh, EF-14040, 2026-06-09). */}
        {!isCancelled && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Legacy `draft` POs (pre-2026-05-18 creates) — let operator promote to `open`. */}
                {selectedPO.status === 'draft' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckIcon />}
                    onClick={() => handleApprove(selectedPO.id)}
                  >
                    Open PO
                  </Button>
                )}
                {/* Send to Supplier — available on any active PO that hasn't been sent yet. */}
                {isPoActive(selectedPO.status) && !selectedPO.sent_at && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={() => handleSend(selectedPO.id)}
                  >
                    Send to Supplier
                  </Button>
                )}
                {/* Record Goods Receipt — available on any active or partially-received PO. */}
                {isPoActive(selectedPO.status) && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<ShippingIcon />}
                    onClick={() => navigate(`/procurement/goods-receipts/new?po_id=${selectedPO.id}`)}
                  >
                    Record Goods Receipt
                  </Button>
                )}
                {/* Void PO — only before goods are received. Operator-facing
                    label is "Void" per Rishabh 2026-05-22; underlying DB
                    status stays 'cancelled' for backwards compatibility. */}
                {['draft', 'open', 'submitted', 'approved', 'sent'].includes(selectedPO.status) && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => { setError(null); setCancelDialogOpen(true); }}
                  >
                    Void PO
                  </Button>
                )}
                {['open', 'sent', 'partially_received', 'completed', 'received'].includes(selectedPO.status) && !selectedPO.is_closed && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CheckIcon />}
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Close this PO?',
                        message: 'Whatever has been received so far is accepted as final — any short or over receipt stays as a variance and is not reconciled. You can reopen the PO later if more goods arrive.',
                        confirmText: 'Close PO',
                        severity: 'warning',
                      });
                      if (!ok) return;
                      try {
                        await closePurchaseOrder(selectedPO.id);
                        setSuccess('PO closed');
                        loadPODetails(selectedPO.id);
                      } catch (err) {
                        setError(err.response?.data?.error || err.message || 'Failed to close PO');
                      }
                    }}
                  >
                    Close PO
                  </Button>
                )}
                {/* Reopen — for a PO that was closed too early (e.g. Cin7-imported
                    POs closed with only a partial qty received) and now needs
                    more receipts. Returns it to an active status. */}
                {(selectedPO.is_closed || ['closed', 'completed', 'received'].includes(selectedPO.status))
                  && selectedPO.status !== 'cancelled' && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Reopen this PO?',
                        message: 'The PO returns to an active status (Open or Partially Received) so more goods receipts can be added against it.',
                        confirmText: 'Reopen PO',
                        severity: 'info',
                      });
                      if (!ok) return;
                      try {
                        await api.post(`${API_BASE}/api/procurement/purchase-orders/${selectedPO.id}/reopen`);
                        setSuccess('PO reopened');
                        loadPODetails(selectedPO.id);
                      } catch (err) {
                        setError(err.response?.data?.error || err.message || 'Failed to reopen PO');
                      }
                    }}
                  >
                    Reopen PO
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* PO Details */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>Line Items</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Disc %</TableCell>
                        <TableCell align="right">Tax %</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                        {selectedPO.status !== 'draft' && <TableCell align="right">Received</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedPO.items || []).map((item, index) => {
                        // Line total: backend stores line_total post-disc post-tax. If
                        // missing (legacy CIN7 imports), reconstruct from qty × price.
                        const lineTotal = (item.line_total != null && Number(item.line_total) > 0)
                          ? Number(item.line_total)
                          : (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
                        const disc = Number(item.discount_percent) || 0;
                        const tax = Number(item.tax_percent) || 0;
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography fontWeight="medium" variant="body2">{item.sku}</Typography>
                            </TableCell>
                            <TableCell>{item.product_name || item.description || '—'}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.unit_price, selectedPO.currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: disc > 0 ? 'text.primary' : 'text.disabled' }}>
                              {disc > 0 ? `${disc.toFixed(1)}%` : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: tax > 0 ? 'text.primary' : 'text.disabled' }}>
                              {tax > 0 ? `${tax.toFixed(1)}%` : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(lineTotal, selectedPO.currency)}
                            </TableCell>
                            {selectedPO.status !== 'draft' && (
                              <TableCell align="right">
                                {item.received_quantity || 0}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={selectedPO.status !== 'draft' ? 6 : 5} />
                        <TableCell align="right">
                          <Typography fontWeight="600">
                            Total: {formatCurrency(selectedPO.total_amount, selectedPO.currency)}
                          </Typography>
                        </TableCell>
                        {selectedPO.status !== 'draft' && <TableCell />}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Attachments — upload/view PDF files against this PO (overview). */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <PoDocuments poId={selectedPO.id} canEdit />
              </CardContent>
            </Card>

            {/* Payments — read-only summary. Source-agnostic: shows CIN7-
                backfilled rows, manual rows, future imports identically.
                Edit/add lives on the edit page (PurchaseOrderEntry). */}
            {poPayments.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h6" fontWeight="600">Payments</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        const paid = poPayments.reduce((s, p) => s + (Number(p.amount_in_po_currency) > 0 ? Number(p.amount_in_po_currency) : Number(p.amount || 0)), 0);
                        const total = Number(selectedPO.total_amount || 0);
                        const remaining = total - paid;
                        return `${poPayments.length} payment${poPayments.length === 1 ? '' : 's'} · ${formatCurrency(paid, selectedPO.currency)} paid of ${formatCurrency(total, selectedPO.currency)} · ${formatCurrency(remaining, selectedPO.currency)} remaining`;
                      })()}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Invoice #</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {poPayments.map(p => (
                          <TableRow key={p.id}>
                            <TableCell>{(p.payment_date || '').slice(0, 10)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {formatCurrency(p.amount, p.currency || selectedPO.currency)}
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{(p.method || '').replace(/_/g, ' ')}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.reference || '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.invoice_number || '—'}</TableCell>
                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{p.notes || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    To add / edit / delete payments, open the PO in Edit view.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Shipments — Tier 1 read-only. Each PO has 1..N shipments; the
                default-shipment auto-created from PO header carries the legacy
                freight/customs/warehouse. Lines show allocated landed cost
                per unit so margin/COGS reporting reflects the true delivered
                cost (vs. just the supplier price). Tier 2 will add the
                multi-shipment split UI + write endpoints. */}
            {shipments.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="600">
                      Shipments {shipments.length > 1 && <Typography component="span" color="textSecondary">({shipments.length})</Typography>}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {shipmentsLoading && <CircularProgress size={16} />}
                      {/* Tier 2: split a new shipment off the default. Hidden on terminal POs. */}
                      {!isPoDone(selectedPO.status) && selectedPO.status !== 'cancelled' && (
                        <Button size="small" variant="outlined" startIcon={<AddIcon fontSize="small" />}
                          onClick={openCreateShipmentDialog} disabled={shipmentBusy}>
                          Add shipment
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {shipments.map(s => {
                    const detail = shipmentDetails[s.id];
                    const isOpen = !!expandedShipments[s.id];
                    const totalAlloc = (Number(s.freight_cost) || 0) + (Number(s.customs_duty) || 0) + (Number(s.insurance_cost) || 0) + (Number(s.other_costs) || 0);
                    return (
                      <Box key={s.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Box
                          onClick={() => toggleShipmentExpansion(s.id)}
                          sx={{ p: 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography fontWeight={600} sx={{ fontFamily: 'monospace' }}>{s.shipment_number}</Typography>
                            <StatusBadge status={s.status} size="small" />
                            {s.destination_warehouse_code && (
                              <Typography variant="caption" color="textSecondary">
                                → {s.destination_warehouse_code}
                              </Typography>
                            )}
                            {s.transport_mode && (
                              <Typography variant="caption" color="textSecondary">
                                · {s.transport_mode}
                              </Typography>
                            )}
                            {s.eta && (
                              <Typography variant="caption" color="textSecondary">
                                · ETA {formatDate(s.eta)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              Landed cost overhead: <strong>{formatCurrency(totalAlloc, s.costs_currency)}</strong>
                            </Typography>
                            {/* Two-stage receipt — Mark Dispatched / Arrived / Cancel.
                                Visible based on shipment.status: planned → Mark Dispatched;
                                shipped → Mark Arrived + Cancel; arrived → Cancel.
                                Posts the GIT JE (Dr 1220 / Cr 2150) on dispatch. */}
                            {selectedPO.status !== 'cancelled' && s.status === 'planned' && (
                              <Button size="small" variant="outlined" color="primary"
                                onClick={(e) => { e.stopPropagation(); openDispatchDialog(s); }}
                                disabled={shipmentBusy}>
                                Mark Dispatched
                              </Button>
                            )}
                            {selectedPO.status !== 'cancelled' && s.status === 'shipped' && (
                              <>
                                <Button size="small" variant="outlined"
                                  onClick={(e) => { e.stopPropagation(); handleMarkArrived(s.id); }}
                                  disabled={shipmentBusy} title="Goods landed at destination port (no JE)">
                                  Mark Arrived
                                </Button>
                                <Button size="small" variant="outlined" color="error"
                                  onClick={(e) => { e.stopPropagation(); handleCancelDispatch(s.id); }}
                                  disabled={shipmentBusy} title="Cancel dispatch — reverses the GIT JE">
                                  Cancel
                                </Button>
                              </>
                            )}
                            {selectedPO.status !== 'cancelled' && s.status === 'arrived' && (
                              <Button size="small" variant="outlined" color="error"
                                onClick={(e) => { e.stopPropagation(); handleCancelDispatch(s.id); }}
                                disabled={shipmentBusy} title="Cancel dispatch — reverses the GIT JE">
                                Cancel
                              </Button>
                            )}
                            {!isPoDone(selectedPO.status) && selectedPO.status !== 'cancelled' && s.status === 'planned' && (
                              <>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditShipmentDialog(s.id); }}
                                  disabled={shipmentBusy} title="Edit freight / customs / dates">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                {/* Default (sequence_no=1) can't be deleted — guarded server-side too. */}
                                {Number(s.sequence_no) !== 1 && (
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteShipment(s.id); }}
                                    disabled={shipmentBusy} title="Delete shipment (returns qty to default)">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </>
                            )}
                            <Typography variant="caption" sx={{ color: 'primary.main' }}>
                              {isOpen ? '▾' : '▸'}
                            </Typography>
                          </Box>
                        </Box>
                        {isOpen && (
                          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            {/* Landed-cost breakdown header */}
                            <Grid container spacing={1} sx={{ mb: 1.5, fontSize: 12 }}>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">Freight</Typography>
                                <Typography fontWeight={500}>{formatCurrency(s.freight_cost, s.costs_currency)}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">Customs duty</Typography>
                                <Typography fontWeight={500}>{formatCurrency(s.customs_duty, s.costs_currency)}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">Insurance</Typography>
                                <Typography fontWeight={500}>{formatCurrency(s.insurance_cost, s.costs_currency)}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="textSecondary">Other costs</Typography>
                                <Typography fontWeight={500}>{formatCurrency(s.other_costs, s.costs_currency)}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">
                                  Allocation method: <strong>{s.allocation_method || 'by_value'}</strong>
                                  {s.carrier && <span> · Carrier: {s.carrier}</span>}
                                  {s.tracking_number && <span> · Tracking: {s.tracking_number}</span>}
                                </Typography>
                              </Grid>
                              {/* Dispatch metadata (visible once status≠'planned'). */}
                              {s.status !== 'planned' && (
                                <Grid item xs={12}>
                                  <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                                      Dispatch info
                                    </Typography>
                                    {s.dispatched_at && (
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Dispatched: <strong>{formatDate(s.dispatched_at)}</strong>
                                        {Number(s.dispatched_value_base_ccy) > 0 && (
                                          <span> · GIT value: <strong>{s.dispatched_value_currency} {Number(s.dispatched_value_base_ccy).toFixed(2)}</strong></span>
                                        )}
                                      </Typography>
                                    )}
                                    {(s.bill_of_lading || s.vessel_or_flight) && (
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        {s.bill_of_lading && <span>BL/AWB: <strong>{s.bill_of_lading}</strong></span>}
                                        {s.vessel_or_flight && <span> · Vessel/Flight: <strong>{s.vessel_or_flight}</strong></span>}
                                      </Typography>
                                    )}
                                    {(s.incoterms || s.port_of_loading || s.port_of_discharge) && (
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        {s.incoterms && <span>Incoterms: <strong>{s.incoterms}</strong></span>}
                                        {s.port_of_loading && <span> · POL: <strong>{s.port_of_loading}</strong></span>}
                                        {s.port_of_discharge && <span> · POD: <strong>{s.port_of_discharge}</strong></span>}
                                      </Typography>
                                    )}
                                    {s.arrived_date && (
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Arrived at destination port: <strong>{formatDate(s.arrived_date)}</strong>
                                      </Typography>
                                    )}
                                    {s.dispatch_je_id && (
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: 10 }}>
                                        GIT journal entry: #{s.dispatch_je_id}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              )}
                              {/* Document slots — supplier CI / packing list / BL.
                                  Always editable so operators can attach docs at any stage. */}
                              <Grid item xs={12}>
                                <Box sx={{ mt: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                    Documents
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {[
                                      { type: 'supplier_ci',    label: 'Supplier CI',    url: s.supplier_ci_url },
                                      { type: 'packing_list',   label: 'Packing List',   url: s.packing_list_url },
                                      { type: 'bill_of_lading', label: 'BL / AWB',       url: s.bill_of_lading_url },
                                    ].map(d => (
                                      <Box key={d.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, minWidth: 90 }}>{d.label}:</Typography>
                                        {d.url ? (
                                          <>
                                            <Button size="small" variant="text" href={shipmentDocumentUrl(s.id, d.type)} target="_blank" rel="noopener">View</Button>
                                            <Button size="small" variant="text" color="error" onClick={(e) => { e.stopPropagation(); handleShipmentDocDelete(s.id, d.type); }} disabled={shipmentBusy}>Remove</Button>
                                          </>
                                        ) : (
                                          <>
                                            <input
                                              type="file"
                                              accept=".pdf,image/png,image/jpeg"
                                              style={{ display: 'none' }}
                                              id={`upload-${s.id}-${d.type}`}
                                              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleShipmentDocUpload(s.id, d.type, f); e.target.value = ''; }}
                                            />
                                            <Button size="small" variant="text" component="label" htmlFor={`upload-${s.id}-${d.type}`} disabled={shipmentBusy}>
                                              Upload
                                            </Button>
                                          </>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                            {/* Per-line allocation */}
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>SKU</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Supplier unit cost</TableCell>
                                    <TableCell align="right">Alloc. freight</TableCell>
                                    <TableCell align="right">Alloc. duty</TableCell>
                                    <TableCell align="right">Landed unit cost</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(detail?.lines || []).map(l => {
                                    const uplift = Number(l.landed_uplift_per_unit) || 0;
                                    return (
                                      <TableRow key={l.id}>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{l.sku}</TableCell>
                                        <TableCell align="right">{Number(l.quantity_shipped).toLocaleString()}</TableCell>
                                        <TableCell align="right">{formatCurrency(l.supplier_unit_cost, s.costs_currency)}</TableCell>
                                        <TableCell align="right">{formatCurrency(l.allocated_freight, s.costs_currency)}</TableCell>
                                        <TableCell align="right">{formatCurrency(l.allocated_customs, s.costs_currency)}</TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2" sx={{ fontWeight: uplift > 0 ? 600 : 400, color: uplift > 0 ? 'warning.dark' : 'text.primary' }}
                                            title={uplift > 0 ? `+${formatCurrency(uplift, s.costs_currency)} landed-cost uplift` : ''}>
                                            {formatCurrency(l.landed_unit_cost, s.costs_currency)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                  {detail?.totals && (
                                    <TableRow>
                                      <TableCell colSpan={2} />
                                      <TableCell align="right">
                                        <Typography variant="caption" color="textSecondary">Supplier total</Typography><br />
                                        {formatCurrency(detail.totals.total_supplier_value, s.costs_currency)}
                                      </TableCell>
                                      <TableCell colSpan={2} align="right">
                                        <Typography variant="caption" color="textSecondary">+ Allocated</Typography><br />
                                        {formatCurrency(detail.totals.total_allocated, s.costs_currency)}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="caption" color="textSecondary">Landed total</Typography><br />
                                        <strong>{formatCurrency(detail.totals.total_landed_value, s.costs_currency)}</strong>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Shipment create/edit dialog (Tier 2). Create mode shows the
                line-split picker reading from the default shipment's detail.
                Edit mode hides the picker and shows only header fields. */}
            <Dialog open={!!shipmentDialog} onClose={() => !shipmentBusy && setShipmentDialog(null)} maxWidth="md" fullWidth>
              <DialogTitle>
                {shipmentDialog?.mode === 'create' ? 'Add shipment (split from default)' : 'Edit shipment'}
              </DialogTitle>
              <DialogContent>
                {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                {/* Line-split picker — only on create. Show each default-shipment
                    line with input for how much qty to move into the new shipment. */}
                {shipmentDialog?.mode === 'create' && (() => {
                  const defaultDetail = shipmentDetails[shipmentDialog?.defaultShipmentId];
                  const defaultLines = defaultDetail?.lines || [];
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Split qty from default shipment → new shipment
                      </Typography>
                      <TableContainer sx={{ maxHeight: 250, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>SKU</TableCell>
                              <TableCell align="right">On default</TableCell>
                              <TableCell align="right">To new shipment</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {defaultLines.map(l => {
                              const max = Number(l.quantity_shipped) || 0;
                              const cur = Number(shipmentSplitLines[l.po_item_id] || 0);
                              return (
                                <TableRow key={l.id}>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{l.sku}</TableCell>
                                  <TableCell align="right">{max.toLocaleString()}</TableCell>
                                  <TableCell align="right">
                                    <TextField size="small" type="number"
                                      inputProps={{ min: 0, max, step: 1, style: { textAlign: 'right' } }}
                                      sx={{ width: 100 }}
                                      value={cur}
                                      onChange={(e) => {
                                        const v = Math.max(0, Math.min(max, Number(e.target.value) || 0));
                                        setShipmentSplitLines(prev => ({ ...prev, [l.po_item_id]: v }));
                                      }} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })()}

                {/* Header form (both create & edit). Two-column grid. */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Destination warehouse"
                      value={shipmentForm.destination_warehouse_code || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, destination_warehouse_code: e.target.value })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" select label="Transport mode"
                      value={shipmentForm.transport_mode || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, transport_mode: e.target.value })}>
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="sea">Sea</MenuItem>
                      <MenuItem value="air">Air</MenuItem>
                      <MenuItem value="road">Road</MenuItem>
                      <MenuItem value="parcel">Parcel</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Carrier"
                      value={shipmentForm.carrier || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, carrier: e.target.value })} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Tracking number"
                      value={shipmentForm.tracking_number || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, tracking_number: e.target.value })} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="date" label="Ex-factory" InputLabelProps={{ shrink: true }}
                      value={shipmentForm.ex_factory_date || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, ex_factory_date: e.target.value })} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="date" label="ETD" InputLabelProps={{ shrink: true }}
                      value={shipmentForm.etd || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, etd: e.target.value })} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" type="date" label="ETA" InputLabelProps={{ shrink: true }}
                      value={shipmentForm.eta || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, eta: e.target.value })} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" type="number" label="Freight"
                      value={shipmentForm.freight_cost ?? 0}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, freight_cost: Number(e.target.value) || 0 })} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" type="number" label="Customs duty"
                      value={shipmentForm.customs_duty ?? 0}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, customs_duty: Number(e.target.value) || 0 })} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" type="number" label="Insurance"
                      value={shipmentForm.insurance_cost ?? 0}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, insurance_cost: Number(e.target.value) || 0 })} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth size="small" type="number" label="Other costs"
                      value={shipmentForm.other_costs ?? 0}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, other_costs: Number(e.target.value) || 0 })} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="Costs currency"
                      value={shipmentForm.costs_currency || 'USD'}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, costs_currency: e.target.value })} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" select label="Allocation method"
                      value={shipmentForm.allocation_method || 'by_value'}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, allocation_method: e.target.value })}>
                      <MenuItem value="by_value">By line value</MenuItem>
                      <MenuItem value="by_weight" disabled>By weight (TBD)</MenuItem>
                      <MenuItem value="by_volume" disabled>By volume / CBM (TBD)</MenuItem>
                      <MenuItem value="flat" disabled>Flat per unit (TBD)</MenuItem>
                      <MenuItem value="manual" disabled>Manual (TBD)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" select label="Status"
                      value={shipmentForm.status || 'planned'}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, status: e.target.value })}>
                      <MenuItem value="planned">Planned</MenuItem>
                      <MenuItem value="in_transit">In transit</MenuItem>
                      <MenuItem value="arrived">Arrived</MenuItem>
                      <MenuItem value="received">Received</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" multiline minRows={2} label="Notes"
                      value={shipmentForm.notes || ''}
                      onChange={(e) => setShipmentForm({ ...shipmentForm, notes: e.target.value })} />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShipmentDialog(null)} disabled={shipmentBusy}>Cancel</Button>
                <Button variant="contained" onClick={submitShipmentDialog} disabled={shipmentBusy}>
                  {shipmentBusy ? 'Saving…' : (shipmentDialog?.mode === 'create' ? 'Create shipment' : 'Save changes')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Mark Dispatched dialog — captures BL/carrier/ETD/ETA/incoterms/ports
                + optional updated freight/customs and posts the GIT journal entry
                (Dr 1220 Inventory in Transit / Cr 2150 GRNI in subsidiary currency).
                After save, shipment flips to 'shipped' and PO to 'in_transit'. */}
            <Dialog open={!!dispatchDialog} onClose={() => !shipmentBusy && setDispatchDialog(null)} maxWidth="md" fullWidth>
              <DialogTitle>
                Mark dispatched — {dispatchDialog?.shipment?.shipment_number}
              </DialogTitle>
              <DialogContent>
                {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Confirms the supplier has shipped these goods. Posts Dr 1220 Inventory in Transit / Cr 2150 GRNI to the subsidiary's books. Freight + customs entered here roll into the destination batch cost when the GR is recorded.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="date" label="Dispatched at"
                      InputLabelProps={{ shrink: true }}
                      value={dispatchForm.dispatched_at || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, dispatched_at: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Bill of Lading / AWB"
                      value={dispatchForm.bill_of_lading || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, bill_of_lading: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Carrier"
                      value={dispatchForm.carrier || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, carrier: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Vessel / Flight"
                      value={dispatchForm.vessel_or_flight || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, vessel_or_flight: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" select label="Incoterms"
                      value={dispatchForm.incoterms || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, incoterms: e.target.value })}>
                      <MenuItem value="">—</MenuItem>
                      {['EXW','FCA','FOB','FAS','CFR','CIF','CPT','CIP','DAP','DPU','DDP'].map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Port of loading"
                      value={dispatchForm.port_of_loading || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, port_of_loading: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Port of discharge"
                      value={dispatchForm.port_of_discharge || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, port_of_discharge: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="date" label="ETD"
                      InputLabelProps={{ shrink: true }}
                      value={dispatchForm.etd || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, etd: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="date" label="ETA at destination"
                      InputLabelProps={{ shrink: true }}
                      value={dispatchForm.eta || ''}
                      onChange={e => setDispatchForm({ ...dispatchForm, eta: e.target.value })} />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="caption" color="textSecondary">Landed cost overhead (in {dispatchForm.costs_currency || 'USD'})</Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="number" label="Freight"
                      inputProps={{ step: 0.01, min: 0 }}
                      value={dispatchForm.freight_cost ?? 0}
                      onChange={e => setDispatchForm({ ...dispatchForm, freight_cost: e.target.value === '' ? 0 : Number(e.target.value) })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="number" label="Customs duty"
                      inputProps={{ step: 0.01, min: 0 }}
                      value={dispatchForm.customs_duty ?? 0}
                      onChange={e => setDispatchForm({ ...dispatchForm, customs_duty: e.target.value === '' ? 0 : Number(e.target.value) })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="number" label="Insurance"
                      inputProps={{ step: 0.01, min: 0 }}
                      value={dispatchForm.insurance_cost ?? 0}
                      onChange={e => setDispatchForm({ ...dispatchForm, insurance_cost: e.target.value === '' ? 0 : Number(e.target.value) })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" type="number" label="Other"
                      inputProps={{ step: 0.01, min: 0 }}
                      value={dispatchForm.other_costs ?? 0}
                      onChange={e => setDispatchForm({ ...dispatchForm, other_costs: e.target.value === '' ? 0 : Number(e.target.value) })} />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDispatchDialog(null)} disabled={shipmentBusy}>Cancel</Button>
                <Button variant="contained" onClick={submitDispatch} disabled={shipmentBusy}>
                  {shipmentBusy ? 'Posting…' : 'Mark dispatched & post GIT JE'}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom>Order Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">PO Date</Typography>
                    <Typography fontWeight="medium">{formatDate(selectedPO.po_date)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Expected Delivery</Typography>
                    <Typography fontWeight="medium">
                      {formatDate(selectedPO.expected_delivery_date) || 'Not set'}
                    </Typography>
                  </Box>
                  {/* Server-derived from the linked receipts (audit D4) — the same
                      first/last_received_date fields the PO list renders, so both
                      screens show one truth. Header actual_delivery_date is not
                      used here (manual-only stamp, wrong/missing on 310/342). */}
                  {selectedPO.first_received_date && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="textSecondary">Goods Received</Typography>
                      <Typography fontWeight="medium" title="Derived from the linked goods receipts">
                        {selectedPO.first_received_date === selectedPO.last_received_date
                          ? formatDate(selectedPO.first_received_date)
                          : `${formatDate(selectedPO.first_received_date)} – ${formatDate(selectedPO.last_received_date)}`}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Currency</Typography>
                    <Typography fontWeight="medium">{selectedPO.currency || companyCurrency}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="textSecondary">Total Amount</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {formatCurrency(selectedPO.total_amount, selectedPO.currency)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Audit D4: receipts linked to this PO — previously never fetched,
                so the detail page had no goods-received information at all. */}
            {poReceipts.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Goods Receipts ({poReceipts.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {poReceipts.map(gr => (
                      <Box key={gr.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="a"
                          onClick={() => navigate(`/procurement/goods-receipts/${gr.id}`)}
                          sx={{ color: 'primary.main', cursor: 'pointer', fontFamily: 'monospace', fontSize: 13, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {gr.grn_number}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {formatDate(gr.receipt_date)}
                          {Number(gr.total_received_qty) > 0 ? ` · ${Number(gr.total_received_qty).toLocaleString()} u` : ''}
                        </Typography>
                        <StatusBadge status={gr.inspection_status || 'pending'} size="small" />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {selectedPO.shipping_address && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>Shipping Address</Typography>
                  <Typography>{selectedPO.shipping_address}</Typography>
                </CardContent>
              </Card>
            )}

            {/* Logistics & Customs read-only panel — shown when any of the
                logistics fields are populated. Operators viewing an existing
                PO need the freight/customs context without entering edit mode. */}
            {(selectedPO.tracking_number || selectedPO.carrier || selectedPO.bill_of_lading
              || selectedPO.incoterms || selectedPO.manufacturing_coo || selectedPO.payment_terms
              || selectedPO.payment_due_date
              || selectedPO.ex_factory_date || selectedPO.port_of_loading
              || selectedPO.port_of_discharge || selectedPO.eta_port_discharge) && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>Logistics & Customs</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, fontSize: 13 }}>
                    {selectedPO.payment_terms && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Payment Terms</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedPO.payment_terms}</Typography>
                      </Box>
                    )}
                    {selectedPO.payment_due_date && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Payment Due</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatDate(selectedPO.payment_due_date)}</Typography>
                      </Box>
                    )}
                    {selectedPO.incoterms && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Incoterms</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedPO.incoterms}</Typography>
                      </Box>
                    )}
                    {selectedPO.manufacturing_coo && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Manufacturing COO</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedPO.manufacturing_coo}</Typography>
                      </Box>
                    )}
                    {selectedPO.carrier && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Carrier</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedPO.carrier}</Typography>
                      </Box>
                    )}
                    {selectedPO.tracking_number && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Tracking</Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{selectedPO.tracking_number}</Typography>
                      </Box>
                    )}
                    {selectedPO.bill_of_lading && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Bill of Lading</Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{selectedPO.bill_of_lading}</Typography>
                      </Box>
                    )}
                    {(selectedPO.ex_factory_date || selectedPO.port_of_loading || selectedPO.port_of_discharge || selectedPO.eta_port_discharge) && (
                      <Divider sx={{ my: 0.5 }} />
                    )}
                    {selectedPO.ex_factory_date && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Ex Factory</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatDate(selectedPO.ex_factory_date)}</Typography>
                      </Box>
                    )}
                    {selectedPO.port_of_loading && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Port of Loading</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedPO.port_of_loading}</Typography>
                      </Box>
                    )}
                    {selectedPO.port_of_discharge && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">Port of Discharge</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedPO.port_of_discharge}</Typography>
                      </Box>
                    )}
                    {selectedPO.eta_port_discharge && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="textSecondary" variant="body2">ETA Discharge</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatDate(selectedPO.eta_port_discharge)}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {selectedPO.notes && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>Notes</Typography>
                  <Typography>{selectedPO.notes}</Typography>
                </CardContent>
              </Card>
            )}

            {/* Activity timeline — audit_log entries for this PO. */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>Activity</Typography>
                <Timeline
                  events={poTimelineEvents.map(e => ({
                    ...e,
                    timestamp: e.timestamp ? formatDate(e.timestamp) + ' ' + (String(e.timestamp).split(' ')[1] || '').slice(0, 5) : '',
                  }))}
                  loading={poTimelineLoading}
                  empty={
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No activity recorded yet.
                    </Typography>
                  }
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Void Dialog — operator copy says "Void" per Rishabh 2026-05-22;
            handler + DB status remain 'cancel'/'cancelled'. */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Void Purchase Order</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography gutterBottom>
              Are you sure you want to void this purchase order? This action cannot be undone.
            </Typography>
            <TextField
              label="Void Reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              required
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>Keep PO</Button>
            <Button variant="contained" color="error" onClick={handleCancel}>
              Void PO
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        {renderPODialog()}
        <ConfirmDialog {...confirmDialogProps} />
      </Box>
    );
  }

  // List View
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Purchase Orders
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Create and manage purchase orders to suppliers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <SavedViews
            storageKey="po-list"
            currentState={{ searchTerm, statusFilter, supplierFilter, brandFilter, currencyFilter, dateFromFilter, dateToFilter }}
            onApply={(s) => {
              setSearchTerm(s.searchTerm || '');
              setSearchInput(s.searchTerm || '');  // mirror to the visible input so loaded view shows in the search box
              setStatusFilter(s.statusFilter || '');
              setSupplierFilter(s.supplierFilter || '');
              setBrandFilter(s.brandFilter || '');
              setCurrencyFilter(s.currencyFilter || '');
              setDateFromFilter(s.dateFromFilter || '');
              setDateToFilter(s.dateToFilter || '');
              setPage(0);
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/procurement/purchase-orders/new')}
          >
            New Purchase Order
          </Button>
          <Tooltip title="Refresh (r)">
            <IconButton onClick={loadPurchaseOrders}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, position: 'relative' }}>
        {/* Subtle 2px progress strip at the top of the filter card during any
            refetch — replaces the prior full-page blanking spinner so the
            table stays visible while filters reload. */}
        {loading && initialLoaded && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
          />
        )}
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Search PO #, supplier, tracking, notes…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter commits immediately (skips the 400ms debounce); Esc
                  // clears the input so operators can wipe a search without
                  // reaching for the mouse.
                  if (e.key === 'Enter') setSearchTerm(searchInput);
                  if (e.key === 'Escape') { setSearchInput(''); setSearchTerm(''); }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              {/* Multi-checkbox supplier filter — replaces the single-select
                  SupplierPicker per Rishabh's request 2026-05-22 (parity with
                  the Status + Currency dropdowns). Typeahead is preserved via
                  Autocomplete `multiple`, with a Checkbox in each option row
                  + chip display for selected items. Comma-joined IDs flow
                  through useUrlState → backend `supplier_id` IN-clause. */}
              {(() => {
                const selectedIds = supplierFilter
                  ? String(supplierFilter).split(',').filter(Boolean).map(s => parseInt(s, 10))
                  : [];
                const selectedSuppliers = suppliers.filter(s => selectedIds.includes(s.id));
                return (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={suppliers.slice().sort((a, b) =>
                      String(a.supplier_name || '').localeCompare(String(b.supplier_name || ''))
                    )}
                    value={selectedSuppliers}
                    onChange={(_, picked) => {
                      const ids = (picked || []).map(p => p.id).filter(Boolean);
                      setSupplierFilter(ids.length ? ids.join(',') : '');
                    }}
                    getOptionLabel={(opt) => opt?.supplier_name || ''}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    PaperComponent={({ children, ...rest }) => {
                      // Sticky Select all / Clear all header.
                      const allIds = (suppliers || []).map(s => s.id).filter(Boolean);
                      const cur = supplierFilter ? supplierFilter.split(',').filter(Boolean).map(Number) : [];
                      const allSelected = cur.length === allIds.length && allIds.length > 0;
                      // Paper imported from MUI elsewhere; we don't have it here.
                      // The popper wrapper accepts any node — using a Box is fine.
                      return (
                        <Box sx={{ bgcolor: 'background.paper', boxShadow: 4, borderRadius: 1, overflow: 'hidden' }} {...rest}>
                          <Box
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSupplierFilter(allSelected ? '' : allIds.join(','));
                            }}
                            sx={{ py: 0.75, px: 1.5, cursor: 'pointer', fontSize: 13, color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            {allSelected ? 'Clear all' : 'Select all'}
                          </Box>
                          {children}
                        </Box>
                      );
                    }}
                    filterOptions={(opts, state) => {
                      const q = (state.inputValue || '').trim().toLowerCase();
                      if (!q) return opts;
                      return opts.filter(o =>
                        (o.supplier_name || '').toLowerCase().includes(q)
                        || (o.supplier_code || '').toLowerCase().includes(q)
                      );
                    }}
                    renderOption={(props, opt, { selected }) => (
                      <li {...props} key={opt.id}>
                        <Checkbox checked={selected} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {opt.supplier_name}
                          </Typography>
                          {opt.supplier_code && (
                            <Typography variant="caption" color="text.secondary">{opt.supplier_code}</Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderTags={(value, getTagProps) => {
                      if (value.length === 1) {
                        return (
                          <Chip
                            {...getTagProps({ index: 0 })}
                            key={value[0].id}
                            label={value[0].supplier_name}
                            size="small"
                            sx={{ maxWidth: 140, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          />
                        );
                      }
                      return (
                        <Chip
                          {...getTagProps({ index: 0 })}
                          key="multi"
                          label={`${value.length} selected`}
                          size="small"
                        />
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Supplier" placeholder={selectedIds.length ? '' : 'All'} />
                    )}
                  />
                );
              })()}
            </Grid>
            {/* Brand multi-checkbox filter. Includes an explicit "Unassigned"
                row for CIN7-imported POs (port script writes brand_id=0 —
                see backend/scripts/cin7-port/port-purchase-orders.js:187).
                Sentinel value '0' represents Unassigned in the URL state. */}
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select
                  multiple
                  value={brandFilter ? brandFilter.split(',').filter(Boolean) : []}
                  label="Brand"
                  onChange={(e) => {
                    // __ALL__ sentinel handled HERE, not in a MenuItem onClick —
                    // MUI Select fires this onChange after the onClick and its
                    // setState clobbers the onClick's, so Select-all never stuck
                    // (same race fixed on the Status filter 2026-05-29; Rishabh
                    // hit it on Brand 2026-07-03).
                    const arr = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    if (arr.includes('__ALL__')) {
                      const all = ['0', ...(brands || []).map(b => String(b.id))];
                      const cur = brandFilter ? brandFilter.split(',').filter(Boolean) : [];
                      setBrandFilter(cur.length === all.length ? '' : all.join(','));
                      return;
                    }
                    setBrandFilter(arr.filter(v => v && v !== '__ALL__').join(','));
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All';
                    if (selected.length === 1) {
                      if (selected[0] === '0') return 'Unassigned';
                      const b = (brands || []).find(x => String(x.id) === selected[0]);
                      return b?.name || `Brand #${selected[0]}`;
                    }
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 520 } } }}
                >
                  {/* Select all / Clear all — toggle logic lives in the Select
                      onChange above (sentinel), so it fires reliably. */}
                  <MenuItem
                    value="__ALL__"
                    sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={(brandFilter ? brandFilter.split(',').filter(Boolean) : []).length === (1 + (brands || []).length) ? 'Clear all' : 'Select all'}
                      primaryTypographyProps={{ sx: { fontSize: 13, color: 'primary.main' } }}
                    />
                  </MenuItem>
                  {/* Unassigned bucket — surfaces CIN7-imported POs that have brand_id=0 */}
                  <MenuItem key="0" value="0" sx={{ py: 0.5 }}>
                    <Checkbox
                      checked={(brandFilter ? brandFilter.split(',').filter(Boolean) : []).includes('0')}
                      size="small" sx={{ p: 0.5, mr: 1 }}
                    />
                    <ListItemText
                      primary="Unassigned (incl. CIN7 imports)"
                      primaryTypographyProps={{ sx: { fontStyle: 'italic', fontSize: 13, color: 'text.secondary' } }}
                    />
                  </MenuItem>
                  {[...(brands || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })).map(b => {
                    const selectedArr = brandFilter ? brandFilter.split(',').filter(Boolean) : [];
                    return (
                      <MenuItem key={b.id} value={String(b.id)} sx={{ py: 0.5 }}>
                        <Checkbox checked={selectedArr.includes(String(b.id))} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText primary={b.name} primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter ? statusFilter.split(',').filter(Boolean) : []}
                  label="Status"
                  onChange={(e) => {
                    // MUI passes the new array on multi-select. Store as comma
                    // string so it round-trips through useUrlState (URL strings).
                    // Select-all / Clear-all are handled HERE (single source of
                    // truth) by detecting the sentinels in the incoming value —
                    // the previous approach used a MenuItem onClick that raced
                    // this onChange and silently undid Select-all (Rishabh bug
                    // 2026-05-29). Return early so a sentinel never persists.
                    const arr = typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value;
                    if (arr.includes('__SELECT_ALL__')) {
                      setStatusFilter(PO_STATUSES.join(','));
                      return;
                    }
                    if (arr.includes('__CLEAR_ALL__')) {
                      setStatusFilter('');
                      return;
                    }
                    const cleaned = arr.filter(v => v && v !== '__SELECT_ALL__' && v !== '__CLEAR_ALL__');
                    setStatusFilter(cleaned.join(','));
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All';
                    if (selected.length === PO_STATUSES.length) return 'All Statuses';
                    if (selected.length === PO_STATUSES.length - 1 && !selected.includes('cancelled')) {
                      return 'All Except Cancelled';
                    }
                    if (selected.length === 1) return selected[0].replace(/_/g, ' ');
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                >
                  {/* Select all + Clear all — explicit, always-present actions.
                      Logic lives in the Select onChange (sentinels), not here,
                      so they fire reliably (Rishabh fix 2026-05-29). */}
                  <MenuItem value="__SELECT_ALL__" sx={{ py: 0.5 }}>
                    <ListItemText
                      primary="Select all"
                      primaryTypographyProps={{ sx: { fontSize: 13, color: 'primary.main' } }}
                    />
                  </MenuItem>
                  <MenuItem
                    value="__CLEAR_ALL__"
                    sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary="Clear all"
                      primaryTypographyProps={{ sx: { fontSize: 13, color: 'primary.main' } }}
                    />
                  </MenuItem>
                  {PO_STATUSES.map(status => {
                    const selectedArr = statusFilter ? statusFilter.split(',').filter(Boolean) : [];
                    const checked = selectedArr.includes(status);
                    return (
                      <MenuItem key={status} value={status} sx={{ py: 0.5 }}>
                        <Checkbox checked={checked} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText
                          primary={status.replace(/_/g, ' ')}
                          primaryTypographyProps={{ sx: { textTransform: 'capitalize', fontSize: 13 } }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Currency</InputLabel>
                <Select
                  multiple
                  notched
                  displayEmpty
                  value={currencyFilter ? currencyFilter.split(',').filter(Boolean) : []}
                  label="Currency"
                  onChange={(e) => {
                    // __ALL__ sentinel handled here, not in a MenuItem onClick —
                    // same onClick/onChange race as the Brand filter above.
                    const arr = typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value;
                    if (arr.includes('__ALL__')) {
                      const all = ['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'];
                      const cur = currencyFilter ? currencyFilter.split(',').filter(Boolean) : [];
                      setCurrencyFilter(cur.length === all.length ? '' : all.join(','));
                      return;
                    }
                    setCurrencyFilter(arr.filter(v => v && v !== '__ALL__').join(','));
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All';
                    if (selected.length === 1) return selected[0];
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                >
                  {/* Select all / Clear all — replaces the earlier `__clear__`
                      sentinel with the platform-wide pattern (Rishabh request
                      2026-05-22). Toggle logic lives in the onChange above. */}
                  <MenuItem
                    value="__ALL__"
                    sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={(currencyFilter ? currencyFilter.split(',').filter(Boolean) : []).length === 11 ? 'Clear all' : 'Select all'}
                      primaryTypographyProps={{ sx: { fontSize: 13, color: 'primary.main' } }}
                    />
                  </MenuItem>
                  {['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'].map(c => {
                    const selectedArr = currencyFilter ? currencyFilter.split(',').filter(Boolean) : [];
                    const checked = selectedArr.includes(c);
                    return (
                      <MenuItem key={c} value={c} sx={{ py: 0.5 }}>
                        <Checkbox checked={checked} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText primary={c} primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <Button variant="outlined" onClick={loadPurchaseOrders} fullWidth size="small">
                Go
              </Button>
            </Grid>
            <Grid item xs={6} md={1.25}>
              {/* Export — streams every matching PO (current filters honoured,
                  no LIMIT) as CSV. Inline blob download because the bot is
                  scoped out of frontend/src/services/ — see check-paths.sh.
                  Widened Grid md from 1 → 1.25 so the Export label doesn't get
                  truncated/overlap with the Go button on wider viewports
                  (Rishabh fix 2026-05-22). */}
              <Button
                variant="outlined"
                fullWidth
                size="small"
                disabled={exporting}
                onClick={async () => {
                  setExporting(true);
                  setError(null);
                  try {
                    const params = {};
                    if (statusFilter) params.status = statusFilter;
                    if (supplierFilter) params.supplier_id = supplierFilter;
                    if (brandFilter) params.brand_id = brandFilter;
                    if (searchTerm) params.search = searchTerm;
                    if (currencyFilter) params.currency = currencyFilter;
                    if (dateFromFilter) params.date_from = dateFromFilter;
                    if (dateToFilter) params.date_to = dateToFilter;
                    const resp = await api.get(`${API_BASE}/api/procurement/purchase-orders/export.csv`, {
                      params,
                      responseType: 'blob',
                    });
                    let filename = `purchase-orders-${new Date().toISOString().slice(0, 10)}.csv`;
                    const cd = resp.headers?.['content-disposition'];
                    if (cd) {
                      const m = cd.match(/filename="?([^"]+)"?/);
                      if (m) filename = m[1];
                    }
                    const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
                    const a = document.createElement('a');
                    a.href = url; a.download = filename;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    setSuccess(`Exported as ${filename}`);
                  } catch (err) {
                    setError(err.response?.data?.error || err.message || 'Export failed');
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                {exporting ? '…' : 'Export'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>PO Date</TableCell>
                  <TableCell>Expected</TableCell>
                  <TableCell>Fully Received</TableCell>
                  <TableCell>Supplier Inv Date</TableCell>
                  <TableCell>Payment Due</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell align="center">Cur</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* No client-side slice — server already returned the page. */}
                {filteredPOs
                  .map((po) => (
                    <TableRow
                      key={po.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/procurement/purchase-orders/${po.id}`)}
                    >
                      <TableCell>
                        <Box
                          component="a"
                          onClick={(e) => { e.stopPropagation(); navigate(`/procurement/purchase-orders/${po.id}`); }}
                          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {po.po_number}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: po.supplier_name ? 'text.primary' : 'text.disabled', fontStyle: po.supplier_name ? 'normal' : 'italic' }}>
                        {po.supplier_name || `Supplier #${po.supplier_id}`}
                      </TableCell>
                      <TableCell>{formatDate(po.po_date)}</TableCell>
                      <TableCell>{formatDate(po.expected_delivery_date) || '—'}</TableCell>
                      {/* Received date: derived from the linked receipts (server-side,
                          audit D4) — the header actual_delivery_date is a manual-only
                          stamp, wrong/missing on 310 of 342 POs with receipts. It is
                          only shown as a fallback when NO receipt exists. */}
                      {(() => {
                        const derived = po.last_received_date;
                        const shown = derived || po.actual_delivery_date;
                        return (
                          <TableCell
                            sx={{ color: shown ? 'success.dark' : 'text.disabled', fontStyle: shown ? 'normal' : 'italic', fontSize: 12 }}
                            title={derived ? (po.first_received_date !== po.last_received_date ? `Received ${formatDate(po.first_received_date)} – ${formatDate(po.last_received_date)} (from receipts)` : 'From linked receipts') : (shown ? 'Manually stamped — no receipt on file' : undefined)}
                          >
                            {shown ? formatDate(shown) : 'pending'}
                          </TableCell>
                        );
                      })()}
                      <TableCell sx={{ color: po.supplier_invoice_date ? 'text.primary' : 'text.disabled', fontStyle: po.supplier_invoice_date ? 'normal' : 'italic', fontSize: 12 }}>
                        {po.supplier_invoice_date ? formatDate(po.supplier_invoice_date) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: po.payment_due_date ? 'text.primary' : 'text.disabled', fontStyle: po.payment_due_date ? 'normal' : 'italic', fontSize: 12 }}>
                        {po.payment_due_date ? formatDate(po.payment_due_date) : '—'}
                      </TableCell>
                      <TableCell align="right">{Number(po.items_count || 0)}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          // Show "received / ordered" with a progress hint.
                          // Pulled from backend's items aggregate subquery.
                          const ord = Number(po.ordered_qty_total || 0);
                          const recv = Number(po.received_qty_total || 0);
                          if (ord === 0) return '—';
                          const pct = Math.round((recv / ord) * 100);
                          const isFull = recv >= ord && ord > 0;
                          // Per-SKU breakdown (JSON string from backend) drives the hover.
                          let breakdown = [];
                          try {
                            const parsed = po.sku_breakdown ? JSON.parse(po.sku_breakdown) : [];
                            breakdown = Array.isArray(parsed) ? parsed : [];
                          } catch (_) { breakdown = []; }
                          const cell = (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, color: isFull ? 'success.dark' : (recv > 0 ? 'warning.dark' : 'text.secondary'), cursor: breakdown.length ? 'help' : 'inherit', textDecoration: breakdown.length ? 'underline dotted' : 'none' }}>
                              {recv.toLocaleString()} / {ord.toLocaleString()} {pct > 0 && `(${pct}%)`}
                            </Typography>
                          );
                          if (!breakdown.length) return cell;
                          const tip = (
                            <div style={{ padding: 4 }}>
                              <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11 }}>Received / Ordered by SKU</div>
                              <table style={{ borderCollapse: 'collapse' }}>
                                <tbody>
                                  {breakdown.map((b, i) => {
                                    const o = Number(b.ordered || 0);
                                    const r = Number(b.received || 0);
                                    const full = o > 0 && r >= o;
                                    return (
                                      <tr key={i}>
                                        <td style={{ paddingRight: 8, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{b.sku}</td>
                                        <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap', color: full ? '#66bb6a' : (r > 0 ? '#ffa726' : '#ccc') }}>{r.toLocaleString()} / {o.toLocaleString()}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                          return <Tooltip title={tip} arrow placement="left">{cell}</Tooltip>;
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(po.total_amount, po.currency || companyCurrency)}
                      </TableCell>
                      {/* Paid / Outstanding in the PO's OWN currency, same basis as Total
                          above — the *_gbp fields mixed US$ and £ on one row (Rishabh 2026-09-04). */}
                      <TableCell align="right">
                        {Number(po.amount_paid) > 0 ? formatCurrency(po.amount_paid, po.currency || companyCurrency) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {['open','partially_received'].includes(po.status) ? formatCurrency(po.outstanding, po.currency || companyCurrency) : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{po.currency || companyCurrency}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={po.status} />
                      </TableCell>
                      <TableCell align="right">
                        {isPoEditable(po.status) && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/procurement/purchase-orders/${po.id}/edit`);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openPoPdf(po, setError); }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredPOs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={15} align="center">
                      <Box sx={{ py: 4 }}>
                        <POIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography color="textSecondary">
                          No purchase orders found
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/procurement/purchase-orders/new')}
                          sx={{ mt: 2 }}
                        >
                          Create First PO
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 250]}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* PO Dialog */}
      {renderPODialog()}
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );

  function renderPODialog() {
    return (
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPO ? 'Edit Purchase Order' : 'New Purchase Order'}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <SupplierPicker
                  value={poForm.supplier_id}
                  onChange={(sid, s) => setPoForm({
                    ...poForm,
                    supplier_id: sid,
                    currency: (s && s.currency) || poForm.currency || companyCurrency,
                  })}
                  suppliers={suppliers}
                  label="Supplier"
                  required
                  size="medium"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={poForm.currency || companyCurrency}
                    label="Currency"
                    onChange={(e) => setPoForm({ ...poForm, currency: e.target.value })}
                  >
                    {['USD','GBP','EUR','AUD','SGD','CAD','NZD','CHF','HKD','JPY','CNY'].map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2.5}>
                <TextField
                  label="PO Date"
                  type="date"
                  value={poForm.po_date}
                  onChange={(e) => setPoForm({ ...poForm, po_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={2.5}>
                <TextField
                  label="Expected Delivery"
                  type="date"
                  value={poForm.expected_delivery_date}
                  onChange={(e) => setPoForm({ ...poForm, expected_delivery_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Supplier auto-fill — read-only context card shown when a supplier
                is picked. Helps the operator verify the right vendor before
                approving (CIN7 puts these fields in editable inputs; we keep
                them read-only since the source of truth is the suppliers screen). */}
            {(() => {
              const s = suppliers.find(x => x.id === poForm.supplier_id);
              if (!s) return null;
              const addr = [s.billing_address_line1, s.billing_address_line2, s.billing_city, s.billing_state, s.billing_postal_code, s.billing_country]
                .filter(Boolean).join(', ');
              if (!addr && !s.primary_contact_email && !s.primary_contact_phone) return null;
              return (
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default', fontSize: 12 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Supplier on file
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {s.primary_contact_name && <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Contact</Typography><Typography variant="body2">{s.primary_contact_name}</Typography></Grid>}
                    {s.primary_contact_email && <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{s.primary_contact_email}</Typography></Grid>}
                    {s.primary_contact_phone && <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{s.primary_contact_phone}</Typography></Grid>}
                    {s.payment_terms && <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Default Terms</Typography><Typography variant="body2">{s.payment_terms}</Typography></Grid>}
                    {addr && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Billing Address</Typography><Typography variant="body2">{addr}</Typography></Grid>}
                  </Grid>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled', fontSize: 10 }}>
                    Read-only — edit on the Suppliers screen
                  </Typography>
                </Paper>
              );
            })()}

            {/* Logistics & Customs — added 2026-05-14 for CIN7 parity.
                Operationally critical for international sourcing; the form
                used to drop these silently into shipping_address or notes. */}
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>Logistics & Customs</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField label="Payment Terms" value={poForm.payment_terms} size="small" fullWidth
                  onChange={(e) => setPoForm({ ...poForm, payment_terms: e.target.value })}
                  placeholder="Net 30 / 50% deposit, 50% on ship" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Tracking Code" value={poForm.tracking_number} size="small" fullWidth
                  onChange={(e) => setPoForm({ ...poForm, tracking_number: e.target.value })}
                  placeholder="Forwarder ref / AWB" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Carrier" value={poForm.carrier} size="small" fullWidth
                  onChange={(e) => setPoForm({ ...poForm, carrier: e.target.value })}
                  placeholder="DHL / FedEx / ocean line" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Bill of Lading" value={poForm.bill_of_lading} size="small" fullWidth
                  onChange={(e) => setPoForm({ ...poForm, bill_of_lading: e.target.value })}
                  placeholder="BOL / Air Waybill #" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Incoterms" value={poForm.incoterms} size="small" fullWidth
                  onChange={(e) => setPoForm({ ...poForm, incoterms: e.target.value })}
                  placeholder="FOB / CIF / DDP / EXW" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Manufacturing COO" value={poForm.manufacturing_coo} size="small" fullWidth
                  onChange={(e) => setPoForm({ ...poForm, manufacturing_coo: e.target.value })}
                  placeholder="ISO-2 (e.g. CN, IN, IT)" />
              </Grid>
              {/* Customs / transit dates — gives ops visibility on factory exit
                  and port ETA for international POs. All optional. */}
              <Grid item xs={6} md={3}>
                <TextField label="Ex Factory Date" type="date" size="small" fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={poForm.ex_factory_date}
                  onChange={(e) => setPoForm({ ...poForm, ex_factory_date: e.target.value })} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Port of Loading" size="small" fullWidth
                  value={poForm.port_of_loading}
                  onChange={(e) => setPoForm({ ...poForm, port_of_loading: e.target.value })}
                  placeholder="e.g. Shenzhen, Shanghai" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="Port of Discharge" size="small" fullWidth
                  value={poForm.port_of_discharge}
                  onChange={(e) => setPoForm({ ...poForm, port_of_discharge: e.target.value })}
                  placeholder="e.g. Long Beach, Felixstowe" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField label="ETA at Discharge Port" type="date" size="small" fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={poForm.eta_port_discharge}
                  onChange={(e) => setPoForm({ ...poForm, eta_port_discharge: e.target.value })} />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Line Items</Typography>
            {poForm.items.map((item, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={2.5}>
                    <TextField
                      label="SKU"
                      value={item.sku}
                      onChange={(e) => handleUpdateItem(index, 'sku', e.target.value)}
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={2.5}>
                    <TextField
                      label="Description"
                      value={item.product_name}
                      onChange={(e) => handleUpdateItem(index, 'product_name', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField
                      label="Supplier SKU"
                      value={item.supplier_sku || ''}
                      onChange={(e) => handleUpdateItem(index, 'supplier_sku', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <TextField
                      label="Qty"
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={1.5}>
                    <TextField
                      label={`Unit (${poForm.currency || 'USD'})`}
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleUpdateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={0.8}>
                    <TextField
                      label="Disc %"
                      type="number"
                      value={item.discount_percent || 0}
                      onChange={(e) => handleUpdateItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={0.8}>
                    <TextField
                      label="Tax %"
                      type="number"
                      value={item.tax_percent || 0}
                      onChange={(e) => handleUpdateItem(index, 'tax_percent', parseFloat(e.target.value) || 0)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                  </Grid>
                  <Grid item xs={1.4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace' }}
                        title={`gross ${(Number(item.quantity)||0) * (Number(item.unit_price)||0)} − disc ${item.discount_percent||0}% + tax ${item.tax_percent||0}%`}>
                        {formatCurrency(computeLineTotal(item), poForm.currency)}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                        disabled={poForm.items.length === 1}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Line notes (optional)"
                      value={item.notes || ''}
                      onChange={(e) => handleUpdateItem(index, 'notes', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="e.g. urgent, swap pack size, sample batch"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Line Item
            </Button>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="h6" color="primary.contrastText">
                Total: {formatCurrency(calculateTotal(), poForm.currency || companyCurrency)}
              </Typography>
            </Box>

            <TextField
              label="Shipping Address"
              value={poForm.shipping_address}
              onChange={(e) => setPoForm({ ...poForm, shipping_address: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Notes (shared with supplier)"
              value={poForm.notes}
              onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Visible on supplier-facing PO documents"
            />
            <TextField
              label="Internal Notes (procurement team only)"
              value={poForm.internal_notes || ''}
              onChange={(e) => setPoForm({ ...poForm, internal_notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Private context — not shared with the supplier"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!poForm.supplier_id || !poForm.items[0]?.sku}
          >
            {editingPO ? 'Update' : 'Create'} PO
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default PurchaseOrders;
